<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import { formatDate } from '../lib/utils';
  import { timeSeriesApi } from '../lib/api';
  import type { ForecastPoint } from '../lib/types';
  import { toString } from '../lib/id-utils';
  
  // Import stores
  import {
    currentTimeSeries,
    forecasts,
    analyses,
    aiAnalyses,
    isLoading,
    error,
    fetchTimeSeries,
    fetchForecasts,
    fetchAnalyses,
    fetchAIAnalyses
  } from '../stores/timeseriesStore';
  
  // Components
  import TimeSeriesChart from '../components/TimeSeriesChart.svelte';
  import DataImport from '../components/DataImport.svelte';
  import DataTable from '../components/DataTable.svelte';
  import ForecastPanel from '../components/ForecastPanel.svelte';
  import AnalysisResults from '../components/AnalysisResults.svelte';
  import AIInsights from '../components/AIInsights.svelte';
  import VisualizationPanel from '../components/VisualizationPanel.svelte';
  import MultiForecasts from '../components/MultiForecasts.svelte';
  
  // Props
  export let id: string | number;
  
  // Ensure consistent ID handling by converting to string
  $: stringId = toString(id);
  
  const dispatch = createEventDispatcher();
  
  let selectedForecastPoints: ForecastPoint[] = [];
  let activeTab = 'overview';
  let editMode = false;
  
  // Edit form data
  let editFormData = {
    name: '',
    description: '',
    frequency: '',
    tags: ''
  };
  
  // Update form data when the time series data changes
  $: if ($currentTimeSeries) {
    editFormData = {
      name: $currentTimeSeries.name,
      description: $currentTimeSeries.description || '',
      frequency: $currentTimeSeries.frequency,
      tags: $currentTimeSeries.tags ? $currentTimeSeries.tags.join(', ') : ''
    };
  }
  
  // Load data on mount
  onMount(() => {
    // Reset any previous data
    resetData();
    
    // Fetch all necessary data using the string representation of the ID
    fetchTimeSeries(stringId, true);
    fetchForecasts(stringId);
    fetchAnalyses(stringId);
    fetchAIAnalyses(stringId);
  });
  
  // Reset data when component is destroyed or when the ID changes
  function resetData() {
    selectedForecastPoints = [];
    activeTab = 'overview';
    editMode = false;
  }
  
  // Handle forecast selection
  function handleForecastSelected(event: CustomEvent) {
    const { forecastId, forecastPoints } = event.detail;
    
    if (forecastId && forecastPoints) {
      selectedForecastPoints = forecastPoints;
    } else {
      selectedForecastPoints = [];
    }
  }
  
  // Handle data import
  function handleDataImported() {
    fetchTimeSeries(stringId, true);
  }
  
  // Handle forecast creation
  function handleForecastCreated() {
    fetchForecasts(stringId);
  }
  
  // Handle AI analysis creation
  function handleAnalysisCreated() {
    fetchAnalyses(stringId);
    fetchAIAnalyses(stringId);
  }
  
  // Update time series
  async function updateTimeSeries() {
    try {
      isLoading.set(true);
      error.set(null);
      
      // Process tags if provided
      const tagsArray = editFormData.tags
        ? editFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];
      
      await timeSeriesApi.update(stringId, {
        name: editFormData.name,
        description: editFormData.description,
        frequency: editFormData.frequency,
        tags: tagsArray
      });
      
      // Reload time series data and exit edit mode
      await fetchTimeSeries(stringId, true);
      editMode = false;
      
    } catch (err) {
      error.set(err instanceof Error ? err : new Error('Failed to update time series'));
      isLoading.set(false);
    }
  }
  
  // Delete time series
  async function deleteTimeSeries() {
    if (!confirm('Are you sure you want to delete this time series? This action cannot be undone.')) {
      return;
    }
    
    try {
      isLoading.set(true);
      error.set(null);
      
      await timeSeriesApi.delete(stringId);
      
      // Navigate back to dashboard
      dispatch('navigate', { page: 'dashboard' });
      
    } catch (err) {
      error.set(err instanceof Error ? err : new Error('Failed to delete time series'));
      isLoading.set(false);
    }
  }
  
  // Computed properties
  $: chartData = $currentTimeSeries?.data_points || [];
  $: hasChartData = chartData.length > 0 || selectedForecastPoints.length > 0;
  
  // Return to dashboard
  function goToDashboard() {
    dispatch('navigate', { page: 'dashboard' });
  }
</script>

<div class="time-series-detail">
  {#if $isLoading && !$currentTimeSeries}
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3">Loading time series data...</p>
    </div>
  {:else if $error}
    <div class="error-container text-center py-5">
      <div class="alert alert-danger mx-auto" style="max-width: 600px;" role="alert">
        <h4 class="alert-heading">Error Loading Time Series</h4>
        <p class="mb-1">
          {#if $error && $error.message}
            {$error.message}
          {:else}
            Error loading time series data
          {/if}
        </p>
        
        {#if $error && typeof $error === 'object'}
          {#if 'responseData' in $error && $error.responseData}
            <div class="mt-3 text-start small">
              <details>
                <summary class="cursor-pointer">Show technical details</summary>
                <div class="bg-light p-2 mt-2 rounded">
                  <pre class="mb-0" style="max-height: 150px; overflow-y: auto; font-size: 0.8rem;">
                    {JSON.stringify($error.responseData, null, 2)}
                  </pre>
                </div>
              </details>
            </div>
          {:else if 'stack' in $error && $error.stack}
            <div class="mt-3 text-start small">
              <details>
                <summary class="cursor-pointer">Show error stack</summary>
                <div class="bg-light p-2 mt-2 rounded">
                  <pre class="mb-0" style="max-height: 150px; overflow-y: auto; font-size: 0.8rem;">
                    {$error.stack}
                  </pre>
                </div>
              </details>
            </div>
          {/if}
        {/if}
        
        <hr>
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-danger" on:click={() => fetchTimeSeries(stringId, true)}>
            <i class="bi bi-arrow-clockwise me-2"></i>
            Try Again
          </button>
          <button class="btn btn-outline-secondary ms-2" on:click={goToDashboard}>
            <i class="bi bi-house me-2"></i>
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  {:else if $currentTimeSeries}
    <div class="mb-4 d-flex justify-content-between align-items-start">
      <div>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <a href="#/" on:click|preventDefault={goToDashboard}>Dashboard</a>
            </li>
            <li class="breadcrumb-item active" aria-current="page">
              {editMode ? 'Edit ' : ''}{$currentTimeSeries.name}
            </li>
          </ol>
        </nav>
        
        {#if !editMode}
          <h2>{$currentTimeSeries.name}</h2>
          <div class="metadata mb-3">
            <span class="badge bg-secondary me-2 text-capitalize">{$currentTimeSeries.frequency}</span>
            <span class="text-muted">{formatDate($currentTimeSeries.created_at || '')}</span>
            <span class="text-muted ms-2">{formatDate($currentTimeSeries.updated_at || '')}</span>
          </div>
          {#if $currentTimeSeries.description}
            <p class="lead">{$currentTimeSeries.description}</p>
          {/if}
          {#if $currentTimeSeries.tags && $currentTimeSeries.tags.length > 0}
            <div class="tags-container mb-3">
              {#each $currentTimeSeries.tags as tag}
                <span class="badge bg-light text-dark me-1 mb-1">{tag}</span>
              {/each}
            </div>
          {/if}
        {:else}
          <div class="edit-form mb-4">
            <form on:submit|preventDefault={updateTimeSeries}>
              <div class="mb-3">
                <label for="name" class="form-label">Name *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="name" 
                  bind:value={editFormData.name}
                  required
                />
              </div>
              
              <div class="mb-3">
                <label for="description" class="form-label">Description</label>
                <textarea 
                  class="form-control" 
                  id="description" 
                  rows="3" 
                  bind:value={editFormData.description}
                ></textarea>
              </div>
              
              <div class="mb-3">
                <label for="frequency" class="form-label">Frequency *</label>
                <select 
                  class="form-select" 
                  id="frequency" 
                  bind:value={editFormData.frequency}
                  required
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              
              <div class="mb-3">
                <label for="tags" class="form-label">Tags</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="tags" 
                  bind:value={editFormData.tags}
                  placeholder="Comma-separated tags (e.g., sales, forecast, finance)"
                />
              </div>
              
              <div class="d-flex justify-content-start">
                <button 
                  type="submit" 
                  class="btn btn-primary" 
                  disabled={$isLoading}
                >
                  {#if $isLoading}
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  {:else}
                    Save Changes
                  {/if}
                </button>
                <button 
                  type="button" 
                  class="btn btn-outline-secondary ms-2" 
                  on:click={() => editMode = false}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        {/if}
      </div>
      
      <div class="action-buttons">
        {#if !editMode}
          <button class="btn btn-outline-primary me-2" on:click={() => editMode = true}>
            <i class="bi bi-pencil me-1"></i>
            Edit
          </button>
          <button class="btn btn-outline-danger" on:click={deleteTimeSeries}>
            <i class="bi bi-trash me-1"></i>
            Delete
          </button>
        {/if}
      </div>
    </div>
    
    <!-- Chart section -->
    <div class="chart-container card mb-4">
      <div class="card-body">
        {#if hasChartData}
          <div class="chart-inner" style="height: 400px;">
            <TimeSeriesChart 
              actualData={chartData} 
              forecastData={selectedForecastPoints}
              title={$currentTimeSeries.name}
            />
          </div>
        {:else}
          <div class="text-center py-5">
            <i class="bi bi-bar-chart-line" style="font-size: 3rem; color: #dee2e6;"></i>
            <p class="mt-3">No data available for this time series yet.</p>
            <p>Import data using the Data Import tab below.</p>
          </div>
        {/if}
      </div>
    </div>
    
    <!-- Tabs for different sections -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'overview' ? 'active' : ''}" 
          on:click={() => activeTab = 'overview'}
        >
          <i class="bi bi-house me-1"></i>
          Overview
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'data' ? 'active' : ''}" 
          on:click={() => activeTab = 'data'}
        >
          <i class="bi bi-table me-1"></i>
          Data
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'visualize' ? 'active' : ''}" 
          on:click={() => activeTab = 'visualize'}
        >
          <i class="bi bi-bar-chart me-1"></i>
          Visualize
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'forecasts' ? 'active' : ''}" 
          on:click={() => activeTab = 'forecasts'}
        >
          <i class="bi bi-graph-up me-1"></i>
          Прогнозы
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'compare' ? 'active' : ''}" 
          on:click={() => activeTab = 'compare'}
        >
          <i class="bi bi-graph-up-arrow me-1"></i>
          Сравнение моделей
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'analyses' ? 'active' : ''}" 
          on:click={() => activeTab = 'analyses'}
        >
          <i class="bi bi-magic me-1"></i>
          AI Analysis
        </button>
      </li>
    </ul>
    
    <!-- Tab content -->
    <div class="tab-content">
      <!-- Overview Tab -->
      <div class="tab-pane {activeTab === 'overview' ? 'active' : ''}">
        <div class="row">
          <div class="col-lg-8">
            <AIInsights 
              timeseriesId={stringId}
              timeseriesData={$currentTimeSeries}
              aiAnalyses={$aiAnalyses}
              on:analysisCreated={handleAnalysisCreated}
            />
          </div>
          
          <div class="col-lg-4">
            <DataImport 
              timeseriesId={stringId}
              frequency={$currentTimeSeries.frequency}
              on:dataImported={handleDataImported}
            />
          </div>
        </div>
      </div>
      
      <!-- Data Tab -->
      <div class="tab-pane {activeTab === 'data' ? 'active' : ''}">
        <div class="row mb-4">
          <div class="col-md-12">
            <DataImport 
              timeseriesId={stringId}
              frequency={$currentTimeSeries.frequency}
              on:dataImported={handleDataImported}
            />
          </div>
        </div>
        
        <div class="row">
          <div class="col-md-12">
            <DataTable 
              data={chartData}
              title="Time Series Data"
              pageSize={10}
              showSearch={true}
              showPagination={true}
              highlightOutliers={true}
            />
          </div>
        </div>
      </div>
      
      <!-- Forecasts Tab -->
      <div class="tab-pane {activeTab === 'forecasts' ? 'active' : ''}">
        <ForecastPanel 
          timeseriesId={stringId}
          timeseriesData={$currentTimeSeries}
          forecasts={$forecasts}
          isLoading={$isLoading}
          on:forecastSelected={handleForecastSelected}
          on:forecastCreated={handleForecastCreated}
        />
      </div>
      
      <!-- Visualization Tab -->
      <div class="tab-pane {activeTab === 'visualize' ? 'active' : ''}">
        <VisualizationPanel 
          timeseriesId={stringId}
          timeseriesData={$currentTimeSeries}
          height={400}
        />
      </div>
      
      <!-- Model Comparison Tab -->
      <div class="tab-pane {activeTab === 'compare' ? 'active' : ''}">
        <MultiForecasts 
          timeseriesId={stringId}
          timeseriesData={$currentTimeSeries}
          height={500}
        />
      </div>
      
      <!-- Analyses Tab -->
      <div class="tab-pane {activeTab === 'analyses' ? 'active' : ''}">
        <AnalysisResults 
          timeseriesId={stringId}
          timeseriesData={$currentTimeSeries}
          analyses={$analyses}
          isLoading={$isLoading}
          on:analysisCreated={handleAnalysisCreated}
        />
      </div>
    </div>
  {/if}
</div>

<style>
  .time-series-detail {
    margin-bottom: 2rem;
  }
  
  h2 {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }
  
  .badge.bg-secondary {
    font-weight: 500;
  }
  
  .lead {
    font-size: 1.1rem;
  }
  
  .chart-container {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .chart-inner {
    width: 100%;
  }
  
  .nav-tabs .nav-link {
    padding: 0.75rem 1.25rem;
    font-weight: 500;
    color: #6c757d;
  }
  
  .nav-tabs .nav-link.active {
    color: var(--primary-color);
    font-weight: 600;
  }
  
  .tab-pane {
    padding: 1.5rem 0;
    display: none;
  }
  
  .tab-pane.active {
    display: block;
  }
  
  .tags-container {
    display: flex;
    flex-wrap: wrap;
  }
  
  .metadata {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  
  .breadcrumb {
    margin-bottom: 0.25rem;
  }
  
  .breadcrumb a {
    color: var(--primary-color);
    text-decoration: none;
  }
  
  .breadcrumb a:hover {
    text-decoration: underline;
  }
  
  .edit-form {
    max-width: 800px;
  }
  
  .cursor-pointer {
    cursor: pointer;
  }
  
  details summary {
    padding: 0.3rem 0;
  }
</style>
